/**
 * Cascade panel — infrastructure failure cascade simulation.
 *
 * Dropdown to select an infrastructure node to "fail", then shows
 * BFS cascade result as an indented tree with depth and impact score.
 * Color-coded by impact: >0.7 red, >0.4 orange, >0.1 yellow.
 */

import { INFRA_NODES, getNodesByType } from '@/config/infrastructure-graph';
import type { InfraNode, InfraType } from '@/config/infrastructure-graph';
import { analyzeCascade } from '@/intelligence/analysis/infrastructure-cascade';
import type { CascadeResult } from '@/intelligence/analysis/types';

export function createCascadePanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'cascade-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  // --- Controls ---
  const controls = document.createElement('div');
  controls.style.cssText = 'margin-bottom: 12px;';

  const label = document.createElement('label');
  label.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); display: block; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em;';
  label.textContent = 'Select Node to Fail';
  controls.appendChild(label);

  // Type filter
  const typeFilter = document.createElement('select');
  typeFilter.style.cssText = `
    font-size: 11px; padding: 4px 8px; border-radius: 4px; margin-bottom: 6px; width: 100%;
    background: var(--bg-secondary); color: var(--text-primary);
    border: 1px solid var(--border-primary);
  `;

  const typeOptions: { value: string; label: string }[] = [
    { value: 'all', label: 'All Types' },
    { value: 'cable', label: 'Undersea Cables' },
    { value: 'pipeline', label: 'Pipelines' },
    { value: 'port', label: 'Ports' },
    { value: 'waterway', label: 'Waterways' },
    { value: 'country', label: 'Countries' },
  ];

  for (const opt of typeOptions) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    typeFilter.appendChild(option);
  }

  controls.appendChild(typeFilter);

  // Node selector
  const nodeSelect = document.createElement('select');
  nodeSelect.style.cssText = `
    font-size: 11px; padding: 4px 8px; border-radius: 4px; margin-bottom: 8px; width: 100%;
    background: var(--bg-secondary); color: var(--text-primary);
    border: 1px solid var(--border-primary);
  `;
  controls.appendChild(nodeSelect);

  // Analyze button
  const analyzeBtn = document.createElement('button');
  analyzeBtn.type = 'button';
  analyzeBtn.style.cssText = `
    font-size: 11px; padding: 6px 12px; border-radius: 4px; cursor: pointer;
    background: var(--accent); color: var(--bg-primary); border: none;
    width: 100%;
  `;
  analyzeBtn.textContent = 'Simulate Failure';
  controls.appendChild(analyzeBtn);

  contentArea.appendChild(controls);

  // Results area
  const resultsArea = document.createElement('div');
  contentArea.appendChild(resultsArea);

  // --- Logic ---
  function populateNodeSelect(typeFilterValue: string): void {
    nodeSelect.textContent = '';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = '-- Select a node --';
    nodeSelect.appendChild(placeholder);

    let nodes: InfraNode[];
    if (typeFilterValue === 'all') {
      nodes = [...INFRA_NODES];
    } else {
      nodes = getNodesByType(typeFilterValue as InfraType);
    }

    nodes.sort((a, b) => a.name.localeCompare(b.name));

    for (const node of nodes) {
      const option = document.createElement('option');
      option.value = node.id;
      option.textContent = `${node.name} (${node.type})`;
      nodeSelect.appendChild(option);
    }
  }

  function getImpactColor(impact: number): string {
    if (impact > 0.7) return 'var(--severity-critical)';
    if (impact > 0.4) return 'var(--severity-high)';
    if (impact > 0.1) return 'var(--severity-medium)';
    return 'var(--severity-low)';
  }

  function renderResult(result: CascadeResult): void {
    resultsArea.textContent = '';

    // Summary
    const summary = document.createElement('div');
    summary.style.cssText = `
      padding: 10px 12px; border-radius: 6px; margin-bottom: 12px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const summaryTitle = document.createElement('div');
    summaryTitle.style.cssText = 'font-size: 12px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px;';
    summaryTitle.textContent = `Cascade from: ${result.sourceNodeName}`;
    summary.appendChild(summaryTitle);

    const statsRow = document.createElement('div');
    statsRow.style.cssText = 'display: flex; gap: 16px; font-size: 11px; color: var(--text-secondary);';

    const affectedStat = document.createElement('span');
    affectedStat.textContent = `${result.affectedNodes.length} nodes affected`;
    statsRow.appendChild(affectedStat);

    const depthStat = document.createElement('span');
    depthStat.textContent = `Max depth: ${result.maxDepth}`;
    statsRow.appendChild(depthStat);

    const impactStat = document.createElement('span');
    impactStat.style.cssText = `font-weight: 600; color: ${getImpactColor(result.totalImpact / Math.max(result.affectedNodes.length, 1))};`;
    impactStat.textContent = `Total impact: ${result.totalImpact.toFixed(2)}`;
    statsRow.appendChild(impactStat);

    summary.appendChild(statsRow);
    resultsArea.appendChild(summary);

    // Affected nodes tree
    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = 'Affected Nodes';
    resultsArea.appendChild(header);

    // Build depth map from paths for indentation
    const depthMap = new Map<string, number>();
    for (const path of result.paths) {
      for (const step of path) {
        const existing = depthMap.get(step.nodeId);
        if (existing === undefined || step.depth < existing) {
          depthMap.set(step.nodeId, step.depth);
        }
      }
    }

    for (const node of result.affectedNodes) {
      const depth = depthMap.get(node.nodeId) ?? 1;
      const indent = depth * 16;
      const color = getImpactColor(node.impact);

      const row = document.createElement('div');
      row.style.cssText = `
        display: flex; align-items: center; gap: 6px; padding: 3px 8px;
        margin-bottom: 2px; border-radius: 4px; background: var(--bg-secondary);
        margin-left: ${indent}px;
      `;

      // Depth connector
      const connector = document.createElement('span');
      connector.style.cssText = 'font-size: 10px; color: var(--text-muted);';
      connector.textContent = depth === 1 ? '\u251C\u2500' : '\u2514\u2500';
      row.appendChild(connector);

      const nodeName = document.createElement('span');
      nodeName.style.cssText = 'font-size: 11px; color: var(--text-primary); flex: 1;';
      nodeName.textContent = node.nodeName;
      row.appendChild(nodeName);

      // Impact bar
      const barBg = document.createElement('div');
      barBg.style.cssText = 'width: 50px; height: 4px; border-radius: 2px; background: var(--bg-primary);';
      const barFill = document.createElement('div');
      barFill.style.cssText = `height: 100%; border-radius: 2px; width: ${Math.min(node.impact * 100, 100)}%; background: ${color};`;
      barBg.appendChild(barFill);
      row.appendChild(barBg);

      const impactLabel = document.createElement('span');
      impactLabel.style.cssText = `font-size: 10px; font-family: var(--font-mono); color: ${color}; min-width: 36px; text-align: right;`;
      impactLabel.textContent = node.impact.toFixed(3);
      row.appendChild(impactLabel);

      resultsArea.appendChild(row);
    }
  }

  function renderNoResult(): void {
    resultsArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; text-align: center; padding: 16px;';
    msg.textContent = 'No downstream impact detected from this node';
    resultsArea.appendChild(msg);
  }

  // --- Event handlers ---
  typeFilter.onchange = () => populateNodeSelect(typeFilter.value);

  analyzeBtn.onclick = () => {
    const nodeId = nodeSelect.value;
    if (!nodeId) return;

    const results = analyzeCascade([nodeId]);
    if (results.length > 0) {
      renderResult(results[0]);
    } else {
      renderNoResult();
    }
  };

  // Initialize
  populateNodeSelect('all');

  // No subscriptions needed — user-driven

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    // No subscriptions to clean up
  };

  return container;
}
